import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useSet } from 'react-use';
import { Accordion } from '../../../../../../../CommonComponents/OPL/Accordion/Accordion/Accordion';
import { AccordionButton } from '../../../../../../../CommonComponents/OPL/Accordion/AccordionButton/AccordionButton';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Separator } from '../../../../../../../CommonComponents/OPL/Separator/Separator';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { PensionChangeOptions } from '../../../../api/useWhatIfOptionsQuery';
import { StyledAccordionPanel } from '../../WhatIf.styles';
import { FormValues } from './PensionChange.meta';
import { PensionChangePanel } from './PensionChangePanel';

interface Props {
  Title: string;
  options: PensionChangeOptions;
}

export const PensionChange = ({ Title, options }: Props) => {
  const [, { has: isPensionCollapsed, toggle: togglePension }] = useSet<string | null>();

  const { control, watch } = useFormContext<FormValues>();

  const { fields } = useFieldArray({
    control,
    name: 'Pensions',
  });

  const watchPensions = watch('Pensions');

  const pensions = fields.map((field, index) => ({
    ...field,
    ...watchPensions[index],
  }));

  const {
    SpousePensionAgeOptions,
    PrincipalPensionAgeOptions,
    PrincipalRetirementAge,
    SpouseRetirementAge,
  } = options;

  return (
    <>
      <Text fontSize="h5" fontWeight={600} px="12" py="16">
        What-if...
      </Text>

      <Separator />

      <Box
        display="flex"
        flexDirection="column"
        p={12}
        css={`
          gap: ${({ theme }) => theme.space['16']}px;
        `}
      >
        <Text fontSize="h6" fontWeight={500}>
          {Title}
        </Text>
      </Box>

      <Accordion isActive={false} css="border: none; border-radius: 0; ">
        {pensions.map((pension, index) => {
          const { IsPrincipal } = pension;

          const retirementAgeForPension = IsPrincipal
            ? PrincipalRetirementAge
            : SpouseRetirementAge ?? PrincipalRetirementAge;

          const ageOptions = (IsPrincipal
            ? PrincipalPensionAgeOptions
            : SpousePensionAgeOptions
          ).map(({ Age, Year }) => ({
            label:
              Age === retirementAgeForPension ? `${Age} - At Retirement` : `${Age}`,
            value: Age,
            ...(Year !== null && { rightLabel: `Year ${Year}` }),
          }));

          const buttonId = `what-if-pension--accordion-button--${pension.id}`;
          const panelId = `what-if-pension--accordion-panel--${pension.id}`;

          return (
            <React.Fragment key={pension.id}>
              <AccordionButton
                id={buttonId}
                panelId={panelId}
                isActive={!isPensionCollapsed(pension.id)}
                onClick={() => togglePension(pension.id)}
              >
                {pension.Name}
              </AccordionButton>

              <StyledAccordionPanel
                id={panelId}
                buttonId={buttonId}
                isActive={!isPensionCollapsed(pension.id)}
              >
                <PensionChangePanel
                  index={index}
                  planTypeOptions={options.PlanTypes}
                  ageOptions={ageOptions}
                  retirementAge={retirementAgeForPension}
                />
              </StyledAccordionPanel>
            </React.Fragment>
          );
        })}
      </Accordion>
    </>
  );
};
